import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Search, Users } from 'lucide-react';
import { UserProfile, UserRole, ROLE_LABELS, getInviteStatus, formatInviteStatus } from '@/types/user';
import { formatDistanceToNow } from 'date-fns';
import { RoleBadge } from './RoleBadge';

interface UserListProps {
  users: UserProfile[];
  onUserClick: (user: UserProfile) => void;
}

export function UserList({ users, onUserClick }: UserListProps) {
  const [searchQuery, setSearchQuery] = useState('');
  const [roleFilter, setRoleFilter] = useState<string>('all');

  // Filter users based on search and role
  const filteredUsers = users.filter((user) => {
    const matchesSearch =
      searchQuery === '' ||
      user.email.toLowerCase().includes(searchQuery.toLowerCase()) ||
      (user.display_name?.toLowerCase().includes(searchQuery.toLowerCase()) ?? false);

    const matchesRole = roleFilter === 'all' || user.role === roleFilter;

    return matchesSearch && matchesRole;
  });

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2 font-serif">
          <Users className="h-5 w-5" />
          Team Members
        </CardTitle>
      </CardHeader>
      <CardContent>
        {/* Filters */}
        <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 mb-4">
          <div className="relative flex-1 sm:max-w-sm">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search by name or email..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-9"
            />
          </div>
          <Select value={roleFilter} onValueChange={setRoleFilter}>
            <SelectTrigger className="w-full sm:w-40">
              <SelectValue placeholder="Filter by role" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Roles</SelectItem>
              <SelectItem value="super_admin">Super Admin</SelectItem>
              <SelectItem value="owner">Owner</SelectItem>
              <SelectItem value="pipeline_manager">Pipeline Manager</SelectItem>
              <SelectItem value="team_lead">Team Lead</SelectItem>
              <SelectItem value="team_member">Team Member</SelectItem>
              <SelectItem value="external_partner">External Partner</SelectItem>
              <SelectItem value="intern">Intern</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Desktop Table View */}
        <div className="hidden md:block rounded-md border">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="text-xs lg:text-sm">Name</TableHead>
                <TableHead className="text-xs lg:text-sm">Email</TableHead>
                <TableHead className="text-xs lg:text-sm">Role</TableHead>
                <TableHead className="text-xs lg:text-sm">Invite Status</TableHead>
                <TableHead className="text-xs lg:text-sm">Last Login</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredUsers.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} className="text-center text-muted-foreground py-8">
                    No users found
                  </TableCell>
                </TableRow>
              ) : (
                filteredUsers.map((user) => (
                  <TableRow
                    key={user.id}
                    className="cursor-pointer hover:bg-muted/50"
                    onClick={() => onUserClick(user)}
                  >
                    <TableCell className="font-medium text-xs lg:text-sm">
                      {user.display_name || '—'}
                    </TableCell>
                    <TableCell className="text-xs lg:text-sm">{user.email}</TableCell>
                    <TableCell>
                      <RoleBadge role={user.role} />
                    </TableCell>
                    <TableCell className="text-muted-foreground text-xs lg:text-sm">
                      {formatInviteStatus(user)}
                    </TableCell>
                    <TableCell className="text-muted-foreground text-xs lg:text-sm">
                      {user.last_login_at
                        ? formatDistanceToNow(new Date(user.last_login_at), { addSuffix: true })
                        : 'Never'}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>

        {/* Mobile Card View */}
        <div className="md:hidden space-y-3">
          {filteredUsers.length === 0 ? (
            <div className="text-center text-muted-foreground py-8">
              No users found
            </div>
          ) : (
            filteredUsers.map((user) => (
              <div
                key={user.id}
                className="p-4 border rounded-lg cursor-pointer hover:bg-muted/50 transition-colors"
                onClick={() => onUserClick(user)}
              >
                <div className="flex justify-between items-start mb-2">
                  <span className="font-medium">{user.display_name || '—'}</span>
                  <RoleBadge role={user.role} />
                </div>
                <p className="text-sm text-muted-foreground truncate mb-2">{user.email}</p>
                <div className="flex justify-between text-xs text-muted-foreground">
                  <span>{formatInviteStatus(user)}</span>
                  <span>
                    {user.last_login_at
                      ? formatDistanceToNow(new Date(user.last_login_at), { addSuffix: true })
                      : 'Never logged in'}
                  </span>
                </div>
              </div>
            ))
          )}
        </div>
      </CardContent>
    </Card>
  );
}
